import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, Button, Alert } from '@mui/material';
import { useMutation } from '@apollo/client';
import { OrganisationModel } from '../../models/organisation/OrganisationModelV2';
import { colorPrimary } from "../../rsc/colors/colors";
import { generateVirtualAccount } from '../../api/orgs';
import '../../styles/pem-card.css';
import { PaymentProvider, VirtualAccount } from '../../models/organisation/PaymentModel';
import { showErrorMessage, showSuccessMessage } from '../../helper/helper';
import { ModalPickVA } from './ModalPickVA';

interface PaymentInformationProps {
    org: OrganisationModel | null;
    onRefetch?: () => void;
}

export type BankType = {
    name: string,
    code: string,
    id: string,
    hasActiveOrPendingVA: boolean
}

export type BankLogoProps = {
    bankNameOrAccount: string | any
    size: 'small' | 'large'
    paymentProviders: PaymentProvider[]
}

export const RenderBankLogo: React.FC<BankLogoProps> = ({paymentProviders, size = 'small', bankNameOrAccount}) => {

    const getBankLogoPath = (bankNameOrAccount: string | any): string | null => {
        let bankName: string;

        if (typeof bankNameOrAccount === 'string') {
            bankName = bankNameOrAccount.toLowerCase();
        } else {
            bankName = bankNameOrAccount.getBankName(paymentProviders).toLowerCase();
        }

        const bankLogoMap: { [key: string]: string } = {
            'bca': 'bank.bca.png',
            'bank central asia': 'bank.bca.png',
            'mandiri': 'bank.mandiri.png',
            'bank mandiri': 'bank.mandiri.png',
            'bni': 'bank.bni.png',
            'bank negara indonesia': 'bank.bni.png',
            'bri': 'bank.bri.png',
            'bank rakyat indonesia': 'bank.bri.png',
            'bsi': 'bank.bsi.png',
            'bank syariah indonesia': 'bank.bsi.png',
            'permata': 'bank.permata.png',
            'bank permata': 'bank.permata.png',
        };

        for (const [key, logoFile] of Object.entries(bankLogoMap)) {
            if (bankName.includes(key)) {
                return `/banks/${logoFile}`;
            }
        }

        return null;
    };

    const logoPath = getBankLogoPath(bankNameOrAccount);
    const bankName = typeof bankNameOrAccount === 'string'
        ? bankNameOrAccount
        : bankNameOrAccount.getBankName(paymentProviders);
    const isSmall = size === 'small';
    const containerClass = isSmall ? 'pem-avatar' : 'pem-bank-logo';


    if (logoPath) {
        return (
            <div className={containerClass}>
                <img
                    src={logoPath}
                    alt={bankName}
                    onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        const fallback = e.currentTarget.parentElement?.querySelector('.fallback-avatar');
                        if (fallback) {
                            (fallback as HTMLElement).style.display = 'flex';
                        }
                    }}
                />
                <div
                    className="fallback-avatar"
                    style={{
                        ...(!isSmall && { width: '40px', height: '40px' }),
                        display: 'none',
                        position: 'absolute' as const
                    }}
                >
                    <span style={{ fontSize: isSmall ? '12px' : '16px' }}>
                        {bankName.charAt(0)}
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div className="pem-avatar-fallback" style={!isSmall ? { width: '40px', height: '40px' } : {}}>
            <span style={{ fontSize: isSmall ? '12px' : '16px' }}>
                {bankName.charAt(0)}
            </span>
        </div>
    );
};

export const PaymentInformation: React.FC<PaymentInformationProps> = ({ org, onRefetch }) => {
    const { t } = useTranslation();
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const [showAddModal, setShowAddModal] = useState(false);

    if (!org?.isMill()) return null;

    // Data processing
    const allVirtualAccounts = org?.payments?.virtualAccounts || [];
    const paymentProviders = org?.payments?.paymentProviders || [];

    const virtualAccounts = allVirtualAccounts.filter(va => {
        const status = va.status?.toLowerCase();
        return status === 'active' || status === 'pending';
    });

    const availableBanks: BankType[] = paymentProviders
        .filter(provider => provider.isAvailable() && provider.accepts_va)
        .map(provider => ({
            name: provider.displayName || provider.legalName,
            code: provider.bankCode,
            id: provider.id,
            hasActiveOrPendingVA: virtualAccounts.some(va => va.provider_id === provider.id)
        }))
        .filter(bank => !bank.hasActiveOrPendingVA);

    // Utility functions
    const formatAccountNumber = (accountNumber: string): string => {
        if (accountNumber?.toLowerCase().includes('pending') || accountNumber?.toLowerCase().includes('assignment')) {
            return accountNumber;
        }
        return accountNumber.replace(/(.{4})/g, '$1 ').trim();
    };

    const getAccountNumberColor = (accountNumber: string): string => {
        if (accountNumber?.toLowerCase().includes('pending') || accountNumber?.toLowerCase().includes('assignment')) {
            return '#999';
        }
        return '#444';
    };

    const copyToClipboard = async (accountNumber: string, id: string) => {
        try {
            await navigator.clipboard.writeText(accountNumber);
            setCopiedId(id);
            setTimeout(() => setCopiedId(null), 2000);
        } catch (err) {
            console.error('Failed to copy: ', err);
        }
    };

    const getStatusColor = (status: string): string => {
        const normalizedStatus = status?.toLowerCase();
        if (normalizedStatus === 'active') return '#4CAF50';
        if (normalizedStatus === 'pending') return '#FFA726';
        return '#f44336';
    };

    return (
        <>
            <div className="flat-box-shadow padding15">
                <div className="pem-header">
                    <span className="greyColor bold pem-title">
                        {t('payment_information')}
                    </span>
                    {availableBanks.length > 0 && (
                        <button
                            onClick={() => setShowAddModal(true)}
                            className="pem-button-primary"
                            style={{ '--color-primary': colorPrimary() } as React.CSSProperties}
                        >
                            <span style={{ fontSize: '14px' }}>+</span>
                        </button>
                    )}
                </div>

                {virtualAccounts.length > 0 ? (
                    <div className="pem-content-flex">
                        {virtualAccounts.map((account) => (
                            <div key={account.id} className="flat-box-shadow padding15 pempem-flex-row gap5">
                                <RenderBankLogo paymentProviders={paymentProviders} bankNameOrAccount={account} size='small'/>

                                <div className="pem-account-info">
                                    <span
                                        className="pem-account-number"
                                        style={{ color: getAccountNumberColor(account.accountNumber) }}
                                    >
                                        {formatAccountNumber(account.accountNumber)}
                                    </span>
                                    {!account.accountNumber?.toLowerCase().includes('pending') &&
                                        !account.accountNumber?.toLowerCase().includes('assignment') && (
                                            <button
                                                onClick={() => copyToClipboard(account.accountNumber, account.id)}
                                                className={`pem-button-copy ${copiedId === account.id ? 'copied' : ''}`}
                                            >
                                                <span style={{
                                                    fontSize: copiedId === account.id ? '10px' : '16px',
                                                    color: copiedId === account.id ? '#4CAF50' : '#667eea',
                                                    fontWeight: 'bold'
                                                }}>
                                                    {copiedId === account.id ? 'Copied' : '⎘'}
                                                </span>
                                            </button>
                                        )}
                                </div>

                                <span className="pem-badge">
                                    {account.amount && account.amount !== 0 
                                        ? `Fixed: ${account.amount.toLocaleString('id-ID')}` 
                                        : 'VA'}
                                </span>

                                <div
                                    className="pem-status-dot"
                                    style={{
                                        backgroundColor: getStatusColor(account.status),
                                        boxShadow: `0 0 6px ${getStatusColor(account.status)}50`
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="pem-empty-state">
                        {t('no_virtual_accounts')}
                    </div>
                )}
            </div>
            <ModalPickVA mode='create' org={org} onBankPicked={() => {}} onRefetch={onRefetch} isOpen={showAddModal} onClose={() => setShowAddModal(false)} availableBanks={availableBanks}/>
        </>
    );
};
